import { React, useState, useParams, useEffect, CloseIcon, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, Grid2, Typography, Box, useApi, formatNumberWithCommas } from '../../../utils/globalImports';
import { CustomButton, CustomTextField, CustomRadioButton, Toaster, validationMessages, FeeStageType } from '../../../utils/globalImports';
import { ApiResponse } from '../../../services/apiService';
import { validateField, validationPatterns } from '../../../utils/validationUtils';

interface ModalProps {
    open: boolean;  // Prop to determine if the modal is open
    onClose: () => void;  // Function to handle modal close
    feeStageData?: ProposalFeeStage;  // Optional prop for editing FeeStage
    afterSubmit: () => void;  // Function to handle modal close
}

interface ProposalFeeStage {
    id?: number;
    proposalId?: number;
    projectId?: number;
    type?: number;
    refrenceNumber?: string;
    description: string;
    unitPrice: string;
    unitPricePer: string;
    status?: boolean;
    completionNotes: string;
    createdId?: number;
    updatedId?: number;
    "Unit Price"?: string;
    "Terms and Notes"?: string;
    "Description"?: string;
    Status?: string;
    quantity?: string;
    proposalValue?: number;
    "Quantity"?: string;
    feeStageTagDescriptions?: [];
    instructedQuantity?: number;
}

const ProposalAddCustomFeeStageModal: React.FC<ModalProps> = ({ open, onClose, feeStageData, afterSubmit }) => {

    const { execute } = useApi();
    const [toast, setToast] = useState({ open: false, message: "", type: "success" });

    const userId = Number(localStorage.getItem("userId"));
    const { projectId } = useParams<{ projectId?: string }>(); // Get projectId from URL
    const { proposalId } = useParams<{ proposalId?: string }>(); // Get proposalId from URL

    const delay = (ms: number | undefined) => new Promise(resolve => setTimeout(resolve, ms));

    // Form data state
    const [formData, setFormData] = useState<ProposalFeeStage>({
        refrenceNumber: '',
        description: '',
        unitPrice: '',
        unitPricePer: '',
        completionNotes: '',
        status: true,
        quantity: '',
        feeStageTagDescriptions: [],
    });

    const [errors, setErrors] = useState({
        refrenceNumber: '',
        description: '',
        value: '',
        valuePer: '',
        completionNotes: '',
        quantity: ''
    });


    useEffect(() => {
        if (open) {
            if (feeStageData) {
                // const [unitPrice, unitPricePer] = (feeStageData["Unit Price"] ?? "").split(',');
                const match = (feeStageData["Unit Price"] ?? "").match(/^(-?[£$€]\d{1,3}(?:,\d{3})*(?:\.\d{2})?)\s+(.*)$/);
                const unitPrice = match?.[1] ?? "";       // e.g., £200.00
                const unitPricePer = match?.[2] ?? ""; // e.g., per Report
                setFormData({
                    description: feeStageData["Description"] || '',
                    unitPrice: unitPrice.replace(/[^0-9.-]/g, "") || '',
                    unitPricePer: unitPricePer?.trim() || '',
                    completionNotes: feeStageData["Terms and Notes"] || '',
                    status: feeStageData.Status == "Active" ? true : false,
                    quantity: feeStageData["Quantity"] || '',
                    feeStageTagDescriptions: feeStageData.feeStageTagDescriptions || [],
                });
            } else {
                setFormData({
                    refrenceNumber: '',
                    description: '',
                    unitPrice: '',
                    unitPricePer: '',
                    completionNotes: '',
                    status: true,
                    feeStageTagDescriptions: []
                });
            }
            setErrors({
                refrenceNumber: '',
                description: '',
                value: '',
                valuePer: '',
                completionNotes: '',
                quantity: ''
            });
        }

    }, [open, feeStageData])

    //Handle form data change
    // const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    //     const { name, value, type, checked } = e.target;

    //     setFormData((prev) => ({
    //         ...prev,
    //         [name]: type === "checkbox" ? checked : type === "radio" ? value === "true" : value,
    //     }));
    // };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type, checked } = e.target;

        // Special handling for unitPrice and quantity to add commas
        if (name === "unitPrice" || name === "quantity") {
            // Remove existing commas
            const rawValue = value.replace(/,/g, '');

            // Allow only digits and decimal point
            if (/^[0-9]*\.?[0-9]*$/.test(rawValue)) {
                setFormData((prev) => ({
                    ...prev,
                    [name]: rawValue ? formatNumberWithCommas(rawValue) : '',
                }));
            }
            return;
        }

        // Default handling for other fields
        setFormData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : type === "radio" ? value === "true" : value,
        }));
    };




    // validate form field
    const validate = () => {

        let isValid = true;
        const errors = { refrenceNumber: '', description: '', value: '', valuePer: '', completionNotes: '', quantity: '' };

        if (!formData.quantity) {
            errors.quantity = validationMessages.common_required("Quantity");
            isValid = false;
        }
        else if (Number(formData.quantity) <= 0) {
            errors.quantity = validationMessages.common_negativeNumber("Quantity");
            isValid = false;
        }


        errors.description = validateField(formData.description, {
            fieldName: "Description",
            required: true,
            maxLength: 5000,
        });

        if (errors.description) isValid = false;

        errors.value = validateField(formData.unitPrice, {
            fieldName: "Unit price",
            required: true,
        });
        if (errors.value) isValid = false;

        errors.valuePer = validateField(formData.unitPricePer, {
            fieldName: "Description Per",
            required: true,
            pattern: validationPatterns.alpha,
        });
        if (errors.valuePer) isValid = false;

        errors.completionNotes = validateField(formData.completionNotes, {
            fieldName: "Terms and Notes",
            maxLength: 5000,
        });

        if (errors.completionNotes) isValid = false;

        setErrors(errors);
        return isValid;
    };

    const handleSubmit = async () => {
        if (validate()) {

            let response: ApiResponse<any>;

            if (feeStageData) {

                const { data } = await execute<ProposalFeeStage>('get', `/Proposal/feeStages/${feeStageData.id}`);
                const updatedProposalFeeStages = {
                    ...data,
                    // id: feeStageData.id,
                    //quantity: Number(formData.quantity).toFixed(2),
                    quantity: Number(String(formData.quantity).replace(/,/g, '')).toFixed(2),
                    // proposalValue: Number(formData.quantity) * Number(formData.unitPrice),
                    description: formData.description,
                    //unitPrice: Number(formData.unitPrice).toFixed(2),
                    unitPrice: Number(String(formData.unitPrice).replace(/,/g, '')).toFixed(2),
                    unitPricePer: formData.unitPricePer,
                    completionNotes: formData.completionNotes,
                    status: formData.status ? 1 : 0, // Update based on Status
                    instructedQuantity: data?.instructedQuantity?.toFixed(1),
                    // feeStageTagDescriptions: formData.feeStageTagDescriptions,
                    updatedId: userId
                };

                //  console.log(updatedProposalFeeStages)
                response = await execute('put', '/Proposal/framework/UpdatefeeStage', updatedProposalFeeStages);
            }
            else {
                // Adding a new Custom FeeStage
                const proposalFeeStages: ProposalFeeStage[] = [
                    {
                        proposalId: Number(proposalId),
                        projectId: Number(projectId),
                        type: FeeStageType.Custom,
                        description: formData.description,
                        //unitPrice: Number(formData.unitPrice).toFixed(2),
                        unitPrice: Number(String(formData.unitPrice).replace(/,/g, '')).toFixed(2),
                        unitPricePer: formData.unitPricePer,
                        //quantity: Number(formData.quantity).toFixed(2),
                        quantity: Number(String(formData.quantity).replace(/,/g, '')).toFixed(2),
                        completionNotes: formData.completionNotes,
                        // feeStageTagDescriptions: formData.feeStageTagDescriptions,
                        createdId: userId
                    }
                ];

                //  console.log(proposalFeeStages)
                response = await execute('post', '/Proposal/framework/AddfeeStage', proposalFeeStages);
            }

            if (response!) {
                if (response.error) {
                    // If there's an error in the response, display the error message
                    setToast({ open: true, message: response.error, type: "error" });
                } else {
                    // If the response is successful, display the success message
                    setToast({ open: true, message: response.data, type: "success" });
                    await delay(2000);
                    afterSubmit();
                    // fetchAllFeeStages();  // Awaiting the API call
                }
            }
        }
    }

    return (
        <div>

            {/* Modal Dialog */}
            <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
                <DialogTitle
                    sx={{
                        position: 'relative',
                        color: '#1e66ab',
                        alignItems: 'center',
                        display: 'flex',
                        justifyContent: 'flex-start',
                        mb: 0,
                        fontWeight: 500,
                        fontSize: '22px'
                    }}>
                    {feeStageData ? 'Edit Fee Stage' : 'Add Custom Fee Stage'}
                    <IconButton
                        color="inherit"
                        onClick={onClose}
                        sx={{
                            position: 'absolute',
                            right: 16, // Position the button at the right
                            top: 16,   // Position the button at the top
                            height: '22px',
                            width: '22px',
                            p: 2,
                            border: '1px solid #d7d7d7',
                            borderRadius: '8px'
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <Divider></Divider>

                <DialogContent>
                    <form onSubmit={handleSubmit}>
                        {/* <Box sx={{ mb: 1 }}>
                            <CustomTextField
                                label="Task Reference Number"
                                name="refrenceNumber"
                                value={formData.refrenceNumber}
                                onChange={handleChange}
                                sx={{ mb: 1 }}
                                required={true}
                            />
                            {errors.refrenceNumber && (
                                <Typography className="validationMessage" role="alert">
                                    {errors.refrenceNumber}
                                </Typography>
                            )}
                        </Box> */}



                        <Box sx={{ mb: 1 }}>
                            <CustomTextField
                                label="Fee Stage Description"
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                sx={{ mb: 1 }}
                                required={true}
                                isTextArea={true}
                            />
                            {errors.description && (
                                <Typography className="validationMessage" role="alert">
                                    {errors.description}
                                </Typography>
                            )}
                        </Box>


                        <Grid2 sx={{ display: 'flex', gap: 5 }}>

                            <Box sx={{ mb: 1 }}>
                                <CustomTextField
                                    label="Unit Price"
                                    name="unitPrice"
                                    type="text"
                                    value={formData.unitPrice}
                                    onChange={handleChange}
                                    sx={{ mb: 1 }}
                                    required={true}
                                />
                                {errors.value && (
                                    <Typography className="validationMessage" role="alert">
                                        {errors.value}
                                    </Typography>
                                )}
                            </Box>
                            <Box sx={{ mb: 1 }}>
                                <CustomTextField
                                    label="Quantity"
                                    name="quantity"
                                    type='text'
                                    value={formData.quantity}
                                    onChange={handleChange}
                                    sx={{ mb: 1 }}
                                    required={true}
                                />
                                {errors.quantity && (
                                    <Typography className="validationMessage" role="alert">
                                        {errors.quantity}
                                    </Typography>
                                )}
                            </Box>



                        </Grid2>

                        <Box sx={{ mb: 1 }}>
                            <CustomTextField
                                //label="Description Per"
                                label="Unit Description"
                                name="unitPricePer"
                                value={formData.unitPricePer}
                                onChange={handleChange}
                                sx={{ mb: 1 }}
                                required={true}
                            />
                            {errors.valuePer && (
                                <Typography className="validationMessage" role="alert">
                                    {errors.valuePer}
                                </Typography>
                            )}
                        </Box>

                        <CustomTextField
                            label="Terms and Notes"
                            name="completionNotes"
                            value={formData.completionNotes}
                            onChange={handleChange}
                            sx={{ mb: 1 }}
                            isTextArea={true}
                        />
                        {errors.completionNotes && (
                            <Typography className="validationMessage" role="alert">
                                {errors.completionNotes}
                            </Typography>
                        )}

                        {feeStageData &&
                            <>
                                {/* radio button */}
                                <Typography sx={{ color: '#1e66ab', fontSize: 14, fontWeight: 500, ml: 1 }}>Status</Typography>
                                <Grid2 sx={{ display: 'flex' }}> {/* Adjust gap as needed */}
                                    {/* Custom Radio Buttons */}
                                    <CustomRadioButton
                                        label="Active"
                                        value="true"
                                        name="status"
                                        checked={formData.status === true}
                                        onChange={handleChange}
                                        required={true}
                                    />
                                    <CustomRadioButton
                                        label="Inactive"
                                        value="false"
                                        name="status"
                                        checked={formData.status === false}
                                        onChange={handleChange}
                                        required={true}
                                    />
                                </Grid2>
                            </>
                        }
                    </form>

                </DialogContent>

                <Divider></Divider>

                <DialogActions sx={{ 
                        px: 3,
                        py: 2,
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: 1.5,
                     }}>
                    {/* Add Fee Stage */}
                    <CustomButton
                        capsule
                        onClick={onClose}
                        sx={{
                            textTransform: 'none',
                            width: 'auto',
                            minWidth: 'fit-content',
                            flex: 'none',
                        }}
                    >
                        Cancel
                    </CustomButton>

                    <CustomButton
                        capsule
                        onClick={handleSubmit}
                        sx={{
                            textTransform: 'none',
                            width: 'auto',
                            minWidth: 'fit-content',
                            flex: 'none',
                        }}
                    >
                        {feeStageData ? 'Update Fee Stage' : 'Add Fee Stage'}
                    </CustomButton>

                </DialogActions>
            </Dialog>

            <Toaster open={toast.open} message={toast.message} type={toast.type as "success" | "error"} onClose={() => setToast({ ...toast, open: false })} />
        </div >
    );

}

export default ProposalAddCustomFeeStageModal;